import type { Opportunity } from "../../lib/crmApi";

export type ProjectedGrain = "month" | "quarter";

export interface ProjectedCard {
  expected_close_date?: string | null;
  value?: number | null;
  stage?: string | null;
}

export interface ProjectedBucket<T extends ProjectedCard = Opportunity> {
  key: string;
  label: string;
  cards: T[];
}

export interface ProjectedChartPoint {
  key: string;
  label: string;
  projected: number;
  target: number;
  count: number;
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const HORIZON_MONTHS = 6;

function parseCloseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const stamp = value.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(stamp);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return new Date(Date.UTC(year, month - 1, day));
}

function monthKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** 1-based fiscal quarter for a 0-based UTC month, given the org's fiscal year start month (1-12). */
function fiscalQuarterOf(utcMonth: number, fiscalStartMonth: number): number {
  const start0 = fiscalStartMonth - 1;
  return Math.floor(((utcMonth - start0 + 12) % 12) / 3) + 1;
}

/** Fiscal year label a date falls in — the calendar year its fiscal Q1 starts in. */
function fiscalYearOf(date: Date, fiscalStartMonth: number): number {
  if (fiscalStartMonth === 1) return date.getUTCFullYear();
  return date.getUTCMonth() + 1 >= fiscalStartMonth ? date.getUTCFullYear() : date.getUTCFullYear() - 1;
}

function quarterKey(date: Date, fiscalStartMonth = 1): string {
  const quarter = fiscalQuarterOf(date.getUTCMonth(), fiscalStartMonth);
  return `${fiscalYearOf(date, fiscalStartMonth)}-Q${quarter}`;
}

export function monthLabel(key: string): string {
  const [year, month] = key.split("-");
  const index = Number(month) - 1;
  return `${MONTH_SHORT[index] ?? month} ${year}`;
}

export function quarterLabel(key: string): string {
  const [year, quarter] = key.split("-");
  return `${quarter} ${year}`;
}

function addMonths(key: string, delta: number): string {
  const [yearRaw, monthRaw] = key.split("-");
  const total = Number(yearRaw) * 12 + (Number(monthRaw) - 1) + delta;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}

/** Next six calendar months starting at the current month (inclusive). */
export function projectedMonthKeys(now: Date = new Date()): string[] {
  const start = monthKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)));
  return Array.from({ length: HORIZON_MONTHS }, (_, index) => addMonths(start, index));
}

/** Quarters covered by the six-month landing horizon. */
export function projectedQuarterKeys(now: Date = new Date(), fiscalStartMonth = 1): string[] {
  const months = projectedMonthKeys(now);
  const keys: string[] = [];
  for (const key of months) {
    const [yearRaw, monthRaw] = key.split("-");
    const monthDate = new Date(Date.UTC(Number(yearRaw), Number(monthRaw) - 1, 1));
    const qKey = quarterKey(monthDate, fiscalStartMonth);
    if (!keys.includes(qKey)) keys.push(qKey);
  }
  return keys;
}

export function projectedHorizonKeys(
  grain: ProjectedGrain,
  now: Date = new Date(),
  fiscalStartMonth = 1,
): string[] {
  return grain === "month" ? projectedMonthKeys(now) : projectedQuarterKeys(now, fiscalStartMonth);
}

export function bucketProjectedPipeline<T extends ProjectedCard>(
  items: T[],
  grain: ProjectedGrain,
  now: Date = new Date(),
  fiscalStartMonth = 1,
  valueOf: (item: T) => number | null | undefined = (item) => item.value,
): ProjectedBucket<T>[] {
  const open = items.filter((item) => (item.stage ?? "") !== "closed_lost");
  const groups = new Map<string, T[]>();
  const unscheduled: T[] = [];

  for (const item of open) {
    const close = parseCloseDate(item.expected_close_date);
    if (!close) {
      unscheduled.push(item);
      continue;
    }
    const key = grain === "month" ? monthKey(close) : quarterKey(close, fiscalStartMonth);
    const bucket = groups.get(key) ?? [];
    bucket.push(item);
    groups.set(key, bucket);
  }

  const range = projectedHorizonKeys(grain, now, fiscalStartMonth);
  const buckets: ProjectedBucket<T>[] = range.map((key) => ({
    key,
    label: grain === "month" ? monthLabel(key) : quarterLabel(key),
    cards: (groups.get(key) ?? []).sort((a, b) => (valueOf(b) ?? 0) - (valueOf(a) ?? 0)),
  }));

  if (unscheduled.length > 0) {
    buckets.push({
      key: "unscheduled",
      label: "No close date",
      cards: unscheduled.sort((a, b) => (valueOf(b) ?? 0) - (valueOf(a) ?? 0)),
    });
  }

  return buckets;
}

/** Chart series for the timed horizon (excludes deals with no close date). */
export function projectedChartPoints<T extends ProjectedCard>(
  buckets: ProjectedBucket<T>[],
  targetForKey: (key: string) => number,
  valueOf: (item: T) => number | null | undefined = (item) => item.value,
): ProjectedChartPoint[] {
  return buckets
    .filter((bucket) => bucket.key !== "unscheduled")
    .map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      projected: bucket.cards.reduce((sum, card) => sum + (valueOf(card) ?? 0), 0),
      target: Math.max(0, targetForKey(bucket.key)),
      count: bucket.cards.length,
    }));
}

export function chartMoney(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}$${Math.round(abs / 1000)}k`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${Math.round(abs)}`;
}
